import React, {
  createContext,
  ReactNode,
  useContext,
  useMemo,
  useState,
} from "react";
import { matchPath, useLocation } from "react-router-dom";
import { routeGroups, RoutePaths } from "../../enumStrings";
import { UserType } from "../../graphql/types";
import { DashboardDensityStyles } from "../common/dashboard/DashboardDensityStyles";
import { resolvePageDescription } from "./pageHeaderDescription";
import { useCmpOrganizationNoun } from "../../contexts/CmpOrganizationNounContext";
import {
  applyCmpOrganizationNoun,
  applyCmpOrganizationNounToPageName,
} from "../../utils/cmpOrganizationNoun";

type PageContainerProps = {
  title?: string;
  description?: string;
  children: React.ReactNode;
};

type PageHeaderOverride = {
  // Optional so pages can inject header actions while keeping the
  // route-configured title/description (e.g. Manage Members).
  title?: string;
  description?: string | null;
  actions?: ReactNode;
  descriptionClassName?: string;
  descriptionStyle?: React.CSSProperties;
  titleClassName?: string;
  titleStyle?: React.CSSProperties;
  // Lets a route family align its header and body to one approved gutter
  // without changing the dashboard-wide PageContainer default.
  horizontalPaddingClassName?: string;
  containerClassName?: string;
  headerSpacingClassName?: string;
  // Renders a full-width divider under the page header block (per design
  // screenshots for pages like Manage Members).
  withHeaderDivider?: boolean;
  headerDividerClassName?: string;
} | null;

type PageHeaderOverrideContextValue = {
  setPageHeaderOverride: (override: PageHeaderOverride) => void;
};

const PageHeaderOverrideContext =
  createContext<PageHeaderOverrideContextValue | null>(null);

export const usePageHeaderOverride = () => {
  const context = useContext(PageHeaderOverrideContext);

  if (!context) {
    throw new Error(
      "usePageHeaderOverride must be used within a PageContainer",
    );
  }

  return context.setPageHeaderOverride;
};

const PageContainer: React.FC<PageContainerProps> = ({
  title: propTitle,
  description: propDescription,
  children,
}) => {
  const location = useLocation();
  const organizationNoun = useCmpOrganizationNoun();
  const [pageHeaderOverride, setPageHeaderOverride] =
    useState<PageHeaderOverride>(null);
  const pageHeaderOverrideContextValue = useMemo(
    () => ({ setPageHeaderOverride }),
    [],
  );

  // Find the route info to get the title
  const findRouteInfo = () => {
    // Check all routes in routeGroups
    const allRoutes = routeGroups[UserType.ChamberAdmin];

    for (const item of allRoutes) {
      if (item.nestedRoute) {
        for (const route of item.nestedRoute.routes) {
          if (matchPath({ path: route.path, end: true }, location.pathname)) {
            return {
              title: route.title || "",
              path: route.path,
              description: route.description || "",
            };
          }
        }
      } else if (item.route) {
        if (
          matchPath({ path: item.route.path, end: true }, location.pathname)
        ) {
          return {
            title: item.route.title || "",
            path: item.route.path,
            description: item.route.description || "",
          };
        }
      }
    }

    return { title: "", path: "", description: "" };
  };

  const routeInfo = findRouteInfo();

  // Use the provided title or get it from the route. Route and page copy
  // says "chamber"; a board of trade sees its own noun in the header.
  const title = applyCmpOrganizationNounToPageName(
    pageHeaderOverride?.title || propTitle || routeInfo.title,
    organizationNoun,
  );
  const resolvedDescription = resolvePageDescription({
    override: pageHeaderOverride?.description,
    prop: propDescription,
    route: routeInfo.description,
  });
  const description = resolvedDescription
    ? applyCmpOrganizationNoun(resolvedDescription, organizationNoun)
    : resolvedDescription;
  const isChamberSetupPage =
    routeInfo.path === RoutePaths.chamberAdmin_myChamber_chamberSettings;
  const titleClassName =
    pageHeaderOverride?.titleClassName || "text-2xl font-bold text-black";
  const descriptionClassName =
    pageHeaderOverride?.descriptionClassName ||
    "text-black text-lg mt-2 whitespace-pre-line";
  const descriptionStyle = pageHeaderOverride?.descriptionStyle;
  const titleStyle = pageHeaderOverride?.titleStyle;
  const headerActions = pageHeaderOverride?.actions ?? null;
  const headerHorizontalPaddingClassName =
    pageHeaderOverride?.horizontalPaddingClassName || "px-8";
  const bodyHorizontalPaddingClassName =
    pageHeaderOverride?.horizontalPaddingClassName || "md:px-8";
  const containerClassName = pageHeaderOverride?.containerClassName || "";
  const headerSpacingClassName =
    pageHeaderOverride?.headerSpacingClassName ||
    (isChamberSetupPage ? "pt-8 pb-0" : "pt-8 pb-4");

  return (
    <>
      <DashboardDensityStyles />
      <div
        className={`bg-white min-h-full flex flex-col ${containerClassName}`}
      >
        <div
          className={`bg-white ${headerHorizontalPaddingClassName} ${
            headerSpacingClassName
          }${
            pageHeaderOverride?.withHeaderDivider
              ? ` border-b ${pageHeaderOverride.headerDividerClassName ?? "border-gray-200"}`
              : ""
          }`}
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1 className={titleClassName} style={titleStyle}>
                {title}
              </h1>
              {description && (
                <p className={descriptionClassName} style={descriptionStyle}>
                  {description}
                </p>
              )}
            </div>
            {headerActions}
          </div>
        </div>
        <div
          className={
            isChamberSetupPage
              ? `flex-1 ${pageHeaderOverride?.horizontalPaddingClassName || "px-0 md:px-8"} py-0`
              : `flex-1 ${bodyHorizontalPaddingClassName} py-4 pt-2`
          }
        >
          <PageHeaderOverrideContext.Provider
            value={pageHeaderOverrideContextValue}
          >
            {children}
          </PageHeaderOverrideContext.Provider>
        </div>
      </div>
    </>
  );
};

export default PageContainer;
